// /partners — my Local Partner surface. Lists my partnerships (a person
// can rep >1 community), and for each shows the businesses in my book
// with member + post counts + commission rate.

import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useApi } from '../lib/api';
import { useAuth } from '../lib/auth';
import { formatMastheadDate, timeAgo } from '../lib/format';

interface Partner {
    id:                 string;
    community_id:       string;
    community_slug:     string;
    community_name:     string;
    display_name:       string;
    contact_email:      string;
    contact_phone?:     string | null;
    agreement_signed_at?: string | null;
    commission_percent: number;
    active:             boolean;
    onboarded_at:       string;
    notes?:             string | null;
}

interface Business {
    partner_id:         string;
    group_id:           string;
    group_slug:         string;
    group_name:         string;
    group_type:         string;
    referred_at:        string;
    commission_percent: number;
    member_count:       number;
    post_count:         number;
}

export function PartnerDashboard() {
    const api = useApi();
    const { authed, login } = useAuth();
    const [partnerships, setPartnerships] = useState<Partner[]>([]);
    const [selected,     setSelected]     = useState<string>('');   // partner.id
    const [book,         setBook]         = useState<Business[]>([]);
    const [showSignup,   setShowSignup]   = useState(false);

    const load = useCallback(async () => {
        if (!authed) return;
        try {
            const r = await api.get<{ partnerships: Partner[] }>('/api/partners/me');
            const ps = r.partnerships ?? [];
            setPartnerships(ps);
            if (ps.length && !selected) setSelected(ps[0].id);
        } catch (e) { console.error('partners load', e); }
    }, [api, authed, selected]);
    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        if (!selected) { setBook([]); return; }
        api.get<{ businesses: Business[] }>('/api/partners/' + selected + '/book')
            .then(r => setBook(r.businesses ?? []))
            .catch(() => setBook([]));
    }, [api, selected]);

    if (!authed) return (
        <div className="prelogin">
            <p>Log in to see your Local Partner dashboard.</p>
            <button type="button" className="plain" onClick={login}>Log in</button>
        </div>
    );

    const cur = partnerships.find(p => p.id === selected);
    const totalMembers = book.reduce((n, b) => n + b.member_count, 0);
    const totalPosts   = book.reduce((n, b) => n + b.post_count, 0);

    return (
        <main>
            <div className="dashboard-wrap">
                <div className="masthead">
                    <div className="rule-top" />
                    <h1>Local Partner</h1>
                    <div className="issue">Your patch · {formatMastheadDate()}</div>
                    <div className="rule-bottom" />
                </div>

                {partnerships.length === 0 ? (
                    <section className="dashboard-block">
                        <p className="empty">
                            You're not registered as a Local Partner for any community yet.
                            Register below to start onboarding businesses in your suburb.
                        </p>
                        {!showSignup && (
                            <div style={{ textAlign: 'center', marginTop: 16 }}>
                                <button type="button" className="plain" onClick={() => setShowSignup(true)}>
                                    Register as a Local Partner
                                </button>
                            </div>
                        )}
                        {showSignup && <SignupForm onCreated={async () => { setShowSignup(false); await load(); }} />}
                    </section>
                ) : (
                    <>
                        {partnerships.length > 1 && (
                            <div className="queue-filter" style={{ margin: '16px 0' }}>
                                {partnerships.map(p => (
                                    <button type="button"
                                            key={p.id}
                                            className={selected === p.id ? 'is-active' : ''}
                                            onClick={() => setSelected(p.id)}>
                                        {p.community_name}
                                    </button>
                                ))}
                            </div>
                        )}

                        {cur && (
                            <>
                                <section className="stat-grid">
                                    <StatCard label="Businesses"   total={book.length} delta={0} />
                                    <StatCard label="Total members" total={totalMembers} delta={0} />
                                    <StatCard label="Posts (all-time)" total={totalPosts} delta={0} />
                                    <StatCard label="Commission" total={cur.commission_percent} delta={0} suffix="%" />
                                </section>

                                <section className="dashboard-block">
                                    <h2>About this partnership</h2>
                                    <dl className="kv">
                                        <dt>Community</dt><dd>{cur.community_name}</dd>
                                        <dt>Public name</dt><dd>{cur.display_name}</dd>
                                        <dt>Contact</dt><dd>{cur.contact_email}{cur.contact_phone ? ' · ' + cur.contact_phone : ''}</dd>
                                        <dt>Agreement</dt><dd>{cur.agreement_signed_at
                                            ? 'Signed ' + timeAgo(cur.agreement_signed_at)
                                            : <span style={{ color: '#8b1919' }}>Not yet signed</span>}</dd>
                                        <dt>Onboarded</dt><dd>{timeAgo(cur.onboarded_at)}</dd>
                                    </dl>
                                </section>

                                <section className="dashboard-block">
                                    <h2>Your book</h2>
                                    {book.length === 0 ? (
                                        <p className="empty">
                                            No businesses attributed to you yet. Share <code>/onboarding?partner={cur.id}</code>
                                            with local business owners — signups through that link count towards your book.
                                        </p>
                                    ) : (
                                        <table className="dashboard-table">
                                            <thead>
                                                <tr><th>Business</th><th>Kind</th><th>Members</th><th>Posts</th><th>Commission</th><th>Referred</th></tr>
                                            </thead>
                                            <tbody>
                                                {book.map(b => (
                                                    <tr key={b.group_id}>
                                                        <td><Link to={'/manage'}>{b.group_name}</Link></td>
                                                        <td className="dashboard-kind">{b.group_type}</td>
                                                        <td>{b.member_count}</td>
                                                        <td>{b.post_count}</td>
                                                        <td>{b.commission_percent}%</td>
                                                        <td>{timeAgo(b.referred_at)}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    )}
                                </section>

                                <section className="dashboard-block">
                                    <h2>Onboard a business</h2>
                                    <p style={{ fontFamily: "'Newsreader',serif", color: '#333' }}>
                                        Share this link with business owners you're pitching. Signups through it
                                        automatically get attributed to your book:
                                    </p>
                                    <div style={{ padding: 12, background: 'var(--paper)', border: '1px solid var(--line)',
                                                  fontFamily: 'monospace', fontSize: 12, wordBreak: 'break-all' }}>
                                        {window.location.origin}/onboarding?partner={cur.id}
                                    </div>
                                </section>
                            </>
                        )}
                    </>
                )}
            </div>
        </main>
    );
}

function StatCard({ label, total, delta, suffix }:
                  { label: string; total: number; delta: number; suffix?: string }) {
    return (
        <div className="stat-card">
            <div className="stat-total">{total}{suffix ?? ''}</div>
            <div className="stat-label">{label}</div>
            {delta > 0 && <div className="stat-delta">+{delta} in last 30d</div>}
        </div>
    );
}

interface Community { id: string; name: string; slug: string }

function SignupForm({ onCreated }: { onCreated: () => void }) {
    const api = useApi();
    const [communities, setCommunities]   = useState<Community[]>([]);
    const [communityID, setCommunityID]   = useState('');
    const [displayName, setDisplayName]   = useState('');
    const [contactEmail, setContactEmail] = useState('');
    const [contactPhone, setContactPhone] = useState('');
    const [commission,   setCommission]   = useState(20);
    const [status, setStatus] = useState<{ tone: 'ok' | 'err' | 'busy'; msg: string } | null>(null);

    useEffect(() => {
        api.get<{ communities: Community[] }>('/api/communities').then(r => {
            setCommunities(r.communities ?? []);
            if (r.communities?.length) setCommunityID(r.communities[0].id);
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    async function onSubmit(e: React.FormEvent) {
        e.preventDefault();
        setStatus({ tone: 'busy', msg: 'Registering…' });
        try {
            await api.post('/api/partners', {
                community_id:       communityID,
                display_name:       displayName,
                contact_email:      contactEmail,
                contact_phone:      contactPhone || null,
                commission_percent: commission,
            });
            setStatus({ tone: 'ok', msg: '✓ Registered' });
            setTimeout(onCreated, 800);
        } catch (err) { setStatus({ tone: 'err', msg: (err as Error).message }); }
    }

    return (
        <form className="new-form" onSubmit={onSubmit} style={{ marginTop: 20 }}>
            <h3>Register as a Local Partner</h3>
            <label>
                <span>Community</span>
                <select required value={communityID} onChange={(e) => setCommunityID(e.target.value)}>
                    {communities.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
            </label>
            <label>
                <span>Your public name (as the partner)</span>
                <input type="text" required value={displayName} onChange={(e) => setDisplayName(e.target.value)}
                       placeholder='e.g. "Ross · Vredehoek OnHere"' />
            </label>
            <label>
                <span>Contact email</span>
                <input type="email" required value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} />
            </label>
            <label>
                <span>Contact phone (optional)</span>
                <input type="tel" value={contactPhone} onChange={(e) => setContactPhone(e.target.value)} />
            </label>
            <label>
                <span>Commission rate (%)</span>
                <input type="number" min={0} max={100} step={0.5}
                       value={commission} onChange={(e) => setCommission(Number(e.target.value))} />
                <em>Default 20%. Agreed rate per partnership deal.</em>
            </label>
            <div className="actions">
                <button type="submit" className="primary">Register</button>
                {status && <span className={status.tone === 'err' ? 'err' : status.tone === 'ok' ? 'ok' : ''}>{status.msg}</span>}
            </div>
        </form>
    );
}
